/**
 * Unified Logout Notice — Feb 2026 (AUTH-RESILIENCE UX cleanup).
 *
 * Single source of truth for "we just logged you out" messaging
 * across all 7 scenarios:
 *
 *   manual            — user clicked the Logout button
 *   idle_timeout      — IdleManager triggered logout after inactivity
 *   token_expired     — backend returned 401 X-Logout-Reason: token_expired
 *   role_changed      — admin changed role mid-session
 *   token_revoked     — token blacklisted server-side
 *   password_changed  — password rotated, all tokens invalidated
 *   session_invalid   — generic fallback (labelled 401 with unknown reason)
 *
 * Lifecycle: the producer (logout(), axios interceptor, IdleManager)
 * calls `queueLogoutNotice(scenario)`. The Login page consumes it
 * ONCE on mount via `readLogoutNotice()` (which deletes the key)
 * and renders a single toast.
 *
 * Storage choice (Feb 2026, round 6):
 *   We use `localStorage` (NOT sessionStorage) because Chrome's
 *   bfcache `pagehide` handler in App.js calls `sessionStorage.clear()`
 *   on every navigation away from a protected page. That would wipe
 *   our notice between queue and the Login page's read. localStorage
 *   survives bfcache eviction.
 *
 *   To prevent cross-tab cross-talk and stale notices we apply a 30s
 *   TTL on the timestamp: a stored notice older than 30s is treated
 *   as stale and skipped (and the key is cleared on read).
 *
 * NOTE: This replaces the legacy `sessionStorage.showLogoutBanner`
 * flag AND the legacy `localStorage.logoutReason` JSON. Both are
 * cleaned up on read for graceful migration from the old code paths.
 */

const STORAGE_KEY = 'titleflow:logoutNotice';
const SHOWN_KEY = 'titleflow:logoutNoticeShown';
const NOTICE_TTL_MS = 30 * 1000; // 30 seconds
const SHOWN_DEDUPE_MS = 60 * 1000; // LOGIN-NOTICE-STALE-001: same scenario shown < 60 s ago → do not replay

// Centralised copy. Keep wording consistent and short.
export const LOGOUT_COPY = {
  manual: {
    icon: '👋',
    title: "You've been logged out",
    body: 'See you again soon!',
    tone: 'info',
  },
  idle_timeout: {
    icon: '⏱',
    title: 'Signed out for inactivity',
    body: 'For your security we logged you out after a period of inactivity.',
    tone: 'info',
  },
  token_expired: {
    icon: '🔑',
    title: 'Your session expired',
    body: 'Please log back in to continue.',
    tone: 'info',
  },
  role_changed: {
    icon: '🛡',
    title: 'Your role was updated',
    body: 'Log in again to pick up the new permissions.',
    tone: 'warning',
  },
  password_changed: {
    icon: '🔒',
    title: 'Password updated',
    body: 'Please log in with your new password.',
    tone: 'warning',
  },
  token_revoked: {
    icon: '⚠',
    title: 'Session revoked',
    body: 'An admin or another session ended this one.',
    tone: 'warning',
  },
  session_invalid: {
    icon: '⚠',
    title: 'Session is no longer valid',
    body: 'Please log in again.',
    tone: 'warning',
  },
};

const VALID_SCENARIOS = new Set(Object.keys(LOGOUT_COPY));

/**
 * Producer-side helper. Called from logout(), the axios interceptor,
 * IdleManager, AdminLayout.handleLogout, etc. — just before redirecting.
 *
 * Idempotent: only the FIRST call within a navigation window wins.
 * This prevents the suppressor flow + interceptor race from queueing
 * two notices.
 */
export const queueLogoutNotice = (scenario, { force = false } = {}) => {
  const normalized = VALID_SCENARIOS.has(scenario) ? scenario : 'session_invalid';
  try {
    // LOGIN-NOTICE-STALE-001: a logout notice only makes sense while a session exists on this
    // device. Once another tab (shared localStorage) or an earlier handler has already cleared
    // the token, late 401s from stragglers/pollers must not re-queue a stale "signed out" notice —
    // that is what made the inactivity toast reappear on every /login refresh. Only server-driven
    // (401-interceptor) scenarios are gated; 'manual' / 'session_invalid' are deliberate local
    // decisions whose callers clear the token first.
    const serverDriven = normalized !== 'manual' && normalized !== 'session_invalid';
    if (!force && serverDriven && !localStorage.getItem('token')) return;
    if (!force && serverDriven && typeof window !== 'undefined' && window.location?.pathname === '/login') return;
    // Don't overwrite an existing FRESH pending notice — first reason wins.
    // (Stale notices are treated as absent and replaced.)
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (parsed && typeof parsed.timestamp === 'number' &&
            Date.now() - parsed.timestamp < NOTICE_TTL_MS) {
          return;
        }
      } catch (_) { /* malformed — overwrite */ }
    }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ scenario: normalized, timestamp: Date.now() })
    );
  } catch (e) {
    console.warn('[logout-notice] queue failed:', e);
    /* localStorage unavailable (private mode, quota exceeded) — silent no-op */
  }
};

/**
 * Consumer-side helper. Called on Login page mount.
 * Returns `{ scenario, timestamp, copy }` or `null`.
 *
 * Single-shot: ALWAYS deletes the key (and legacy keys) on read so
 * the notice fires exactly once. Notices older than `NOTICE_TTL_MS`
 * are treated as stale (returned as null but still cleaned up).
 */
export const readLogoutNotice = () => {
  let payload = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && VALID_SCENARIOS.has(parsed.scenario)) {
        const ts = typeof parsed.timestamp === 'number' ? parsed.timestamp : 0;
        const age = Date.now() - ts;
        if (age >= 0 && age < NOTICE_TTL_MS) {
          payload = {
            scenario: parsed.scenario,
            timestamp: ts,
            copy: LOGOUT_COPY[parsed.scenario],
          };
        }
      }
    }
  } catch (_) {
    /* malformed — fall through to legacy migration */
  }

  // Legacy migration: previous sessions used either
  //   sessionStorage.showLogoutBanner === 'true'  (manual logout), or
  //   localStorage.logoutReason JSON              (idle / interceptor)
  // OR the previous sessionStorage-based notice (round 5 storage).
  // Translate them so users coming from an old bundle still see
  // a toast.
  if (!payload) {
    try {
      const oldSession = sessionStorage.getItem(STORAGE_KEY);
      if (oldSession) {
        const parsed = JSON.parse(oldSession);
        if (parsed && VALID_SCENARIOS.has(parsed.scenario)) {
          payload = {
            scenario: parsed.scenario,
            timestamp: parsed.timestamp || Date.now(),
            copy: LOGOUT_COPY[parsed.scenario],
          };
        }
      }
    } catch (_) { /* noop */ }
  }
  if (!payload) {
    try {
      if (sessionStorage.getItem('showLogoutBanner') === 'true') {
        payload = {
          scenario: 'manual',
          timestamp: Date.now(),
          copy: LOGOUT_COPY.manual,
        };
      }
    } catch (_) { /* noop */ }
  }
  if (!payload) {
    try {
      const legacy = localStorage.getItem('logoutReason');
      if (legacy) {
        const parsed = JSON.parse(legacy);
        // Stale notices > 5 min are ignored.
        if (
          parsed &&
          typeof parsed.timestamp === 'number' &&
          Date.now() - parsed.timestamp < 5 * 60 * 1000
        ) {
          payload = {
            scenario: 'idle_timeout',
            timestamp: parsed.timestamp,
            copy: LOGOUT_COPY.idle_timeout,
          };
        }
      }
    } catch (_) { /* noop */ }
  }

  // LOGIN-NOTICE-STALE-001: never replay a scenario that was already displayed moments ago
  // (refresh of /login, or a re-queued straggler notice). A genuine new logout needs a new
  // login first, which is impossible inside the dedupe window.
  if (payload) {
    try {
      const shown = JSON.parse(localStorage.getItem(SHOWN_KEY) || 'null');
      if (shown && shown.scenario === payload.scenario &&
          typeof shown.at === 'number' && Date.now() - shown.at < SHOWN_DEDUPE_MS) {
        clearLogoutNotice();
        payload = null;
      }
    } catch (_) { /* noop */ }
  }

  // Always clean up — single-shot semantics. Clean BOTH storages
  // (current + legacy) so a stale entry can't resurface later.
  // PREMIUM-NOTIFY-001 — peek only; the Login page calls clearLogoutNotice() once the toast is shown, so
  // intermediate reloads (interceptor + ProtectedRoute racing to /login) cannot swallow the notice.
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
  try { sessionStorage.removeItem('showLogoutBanner'); } catch (_) { /* noop */ }
  try { localStorage.removeItem('logoutReason'); } catch (_) { /* noop */ }

  return payload;
};

/**
 * Test helper — used by IdleManager / interceptor unit tests.
 * Reads from current (localStorage) location.
 */
export const _peekLogoutNoticeRaw = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
};

/** PREMIUM-NOTIFY-001 — consume the queued notice (called after the toast is displayed). */
export const clearLogoutNotice = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
};

/** LOGIN-NOTICE-STALE-001 — remember what was just displayed so refreshes/stragglers cannot replay it. */
export const markLogoutNoticeShown = (scenario) => {
  try { localStorage.setItem(SHOWN_KEY, JSON.stringify({ scenario, at: Date.now() })); } catch (_) { /* noop */ }
};

/** Called on successful login: a fresh session must never inherit a stale notice. */
export const resetLogoutNoticeState = () => {
  clearLogoutNotice();
  try { localStorage.removeItem(SHOWN_KEY); } catch (_) { /* noop */ }
};
